/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { Close } from '@ahamove/icons';
import { Cash } from '@ahamove/icons/order';
import { MINI_APP_LIST, useAuth } from '@ahm/react-auth';
import { Avatar, Button, Group, Modal, Stack, Text, rem } from '@mantine/core';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState, useCallback } from 'react';
import PaymentSelectionModal from '@/components/modals/payment-modal/payment-selection-modal';
import { usePaymentMethodHook } from '@/hooks';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { convertPaymentMethodNumber } from '@/lib/utils';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export default function PaymentSelection() {
  const tPayment = useTranslations('payment');
  const localSuffix = useLocaleSuffix();
  const isMobile = useIsMobile();
  const { webInAppConfig } = useAuth();
  const { initPaymentMethod } = usePaymentMethodHook();
  const { isLoadingStore, paymentMethod: selectedPaymentMethod } =
    useBookingStore((state) => state);

  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    initPaymentMethod();
  }, [initPaymentMethod]);

  const paymentTitle = useMemo(() => {
    if (!selectedPaymentMethod) return '';
    const cardNumber =
      selectedPaymentMethod?.extra_data?.card_number ||
      selectedPaymentMethod?.extra_data?.masked_payment_identifier;

    const shortName = selectedPaymentMethod?.[`short_name_${localSuffix}`];
    const name = selectedPaymentMethod?.[`name_${localSuffix}`];

    return cardNumber
      ? convertPaymentMethodNumber(cardNumber)
      : shortName || name || '';
  }, [localSuffix, selectedPaymentMethod]);

  const handleOpenModal = useCallback(() => {
    if (webInAppConfig?.id && MINI_APP_LIST.includes(webInAppConfig.id)) return;
    setIsOpen(true);
  }, [webInAppConfig]);

  return (
    <>
      <Button
        disabled={isLoadingStore}
        onClick={handleOpenModal}
        justify={isMobile ? 'center' : 'left'}
        variant={isMobile ? 'white' : 'default'}
        px={isMobile ? rem(4) : rem(12)}
        py={rem(8)}
        h={rem(40)}
        fullWidth
      >
        <Stack
          gap={rem(2)}
          ta="left"
          justify="left"
          flex={1}
          style={{ whiteSpace: 'pre-line', overflow: 'hidden' }}
        >
          <Group
            gap={isMobile ? rem(4) : rem(8)}
            align="center"
            wrap="nowrap"
            style={{
              justifyContent: isMobile ? 'center' : 'unset',
              display: isMobile ? 'flex' : 'inline-flex',
            }}
          >
            {paymentTitle ? (
              <>
                <Avatar
                  radius="sm"
                  size={20}
                  src={selectedPaymentMethod?.icon_url}
                  alt={selectedPaymentMethod?.method_id}
                />
                <Text
                  c="neutral.7"
                  fw={isMobile ? 700 : 500}
                  size="sm"
                  flex={1}
                  truncate
                >
                  {paymentTitle}
                </Text>
              </>
            ) : (
              <>
                <Cash className="size-5" />
                <Text
                  c="neutral.7"
                  fw={isMobile ? 700 : 500}
                  size="sm"
                  flex={1}
                  truncate
                >
                  {tPayment('method.cash')}
                </Text>
              </>
            )}
          </Group>
        </Stack>
      </Button>

      <Modal
        centered
        styles={{
          root: { zIndex: 1 },
          title: {
            flex: 1,
            marginLeft: 0,
            textAlign: isMobile ? 'center' : 'left',
            fontSize: 18,
            fontWeight: 700,
          },
          header: {
            minHeight: isMobile ? rem(45) : rem(56),
            padding: isMobile ? '0px 16px' : '0px 24px',
            borderBottom: '1px solid var(--mantine-color-neutral-0)',
            background: 'white',
          },
          body: { padding: 0 },
          content: {
            minHeight: rem(600),
            backgroundColor: 'var(--mantine-color-neutral-0)',
          },
        }}
        closeButtonProps={{
          pos: isMobile ? 'absolute' : 'unset',
          mr: isMobile ? 0 : rem(-10),
          left: rem(12),
          top: isMobile ? 'auto' : rem(8),
          radius: 'xl',
          icon: (
            <Close
              width={22}
              height={22}
              color="var(--mantine-color-neutral-7)"
            />
          ),
        }}
        fullScreen={isMobile}
        closeOnClickOutside={false}
        title={tPayment('select_method.select_payment_method')}
        opened={isOpen}
        onClose={close}
      >
        <PaymentSelectionModal onClose={close} />
      </Modal>
    </>
  );
}
